import React from "react";
import { motion } from "framer-motion";
import {
  XCircle,
  X,
  AlertCircle,
  DollarSign,
  RefreshCw,
  ArrowLeft,
} from "lucide-react";
import useModal from "hooks/use-modal";
import { formatMoney } from "../../utils/formatMoney";

const ModalSettlementDeclined = (props) => {
  const { account, submitAmount, error } = props.modalProps;
  const { openModal, closeModal, modalTypes } = useModal();

  // Animation variants
  const contentAnimation = {
    hidden: { opacity: 0, y: 10 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3,
        staggerChildren: 0.1,
      },
    },
  };

  const itemAnimation = {
    hidden: { opacity: 0, scale: 0.9 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 15,
      },
    },
  };

  const handleReviseOffer = () => {
    // Go directly to ModalMakePayment with settlement-offer mode to revise the amount
    openModal(modalTypes.ModalMakePayment, {
      account,
      amount: "", // Clear amount so they can enter a new one
      mode: "settlement-offer",
      back: modalTypes.ModalOffer,
      backData: {
        account,
        offers: null,
        mode: "settlement-offer",
      },
    });
  };

  const handleMakePayment = () => {
    openModal(modalTypes.ModalMakePayment, {
      account,
      mode: "one-time",
    });
  };

  return (
    <div className="flex flex-col h-full max-h-[100vh] sm:max-h-[90vh] p-0">
      {/* Modal Header - Error Red Gradient */}
      <div className="relative bg-gradient-to-r from-red-600 to-rose-700 px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8 flex-shrink-0 sm:rounded-t-2xl">
        <button
          onClick={closeModal}
          className="absolute top-3 right-3 sm:top-4 sm:right-4 p-1.5 sm:p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors group z-10"
        >
          <X className="w-4 h-4 sm:w-5 sm:h-5 text-white group-hover:rotate-90 transition-transform duration-300" />
        </button>

        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="flex items-center gap-3 sm:gap-4 pr-10"
        >
          <div className="w-10 h-10 sm:w-12 sm:h-12 lg:w-14 lg:h-14 bg-white/20 backdrop-blur rounded-xl flex items-center justify-center flex-shrink-0">
            <XCircle className="w-5 h-5 sm:w-6 sm:h-6 lg:w-7 lg:h-7 text-white" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-white">
              Offer Not Accepted
            </h2>
            <p className="text-red-100 text-sm sm:text-base">
              We're unable to accept this offer amount
            </p>
          </div>
        </motion.div>
      </div>

      {/* Modal Body */}
      <motion.div
        className="p-4 sm:p-6 lg:p-8 overflow-y-auto flex-1 min-h-0 overscroll-contain bg-gray-900"
        variants={contentAnimation}
        initial="hidden"
        animate="visible"
      >
        {/* Error Message Card */}
        <motion.div
          variants={itemAnimation}
          className="bg-red-500/10 border border-red-500/30 rounded-xl p-6 mb-6"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-red-500/20 rounded-full flex items-center justify-center">
              <AlertCircle className="w-6 h-6 text-red-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white">
                Offer Declined
              </h3>
              <p className="text-gray-400 text-sm">
                {error || "Your offer was not accepted"}
              </p>
            </div>
          </div>

          <p className="text-gray-300">
            Unfortunately, we're unable to accept your current offer. You may
            revise your offer amount or choose a different payment option.
          </p>
        </motion.div>

        {/* Offer Details */}
        <motion.div
          variants={itemAnimation}
          className="bg-gray-800/50 backdrop-blur rounded-xl p-6 mb-6 border border-gray-700/50"
        >
          <h4 className="text-white font-semibold mb-4 flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-gray-400" />
            Declined Offer Details
          </h4>

          <div className="space-y-3">
            <div className="flex justify-between items-center py-2 border-b border-gray-700/50">
              <span className="text-gray-400 text-sm">Offered Amount</span>
              <span className="text-xl font-bold text-red-400 line-through">
                ${formatMoney(submitAmount)}
              </span>
            </div>

            <div className="flex justify-between items-center py-2">
              <span className="text-gray-400 text-sm">Status</span>
              <span className="px-3 py-1 bg-red-500/20 text-red-400 text-sm font-medium rounded-full">
                Not Accepted
              </span>
            </div>
          </div>
        </motion.div>

        {/* Suggestion Card */}
        <motion.div
          variants={itemAnimation}
          className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4 mb-6"
        >
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-blue-300 font-medium mb-1">
                What You Can Do Next
              </p>
              <ul className="text-xs text-blue-200 space-y-1">
                <li>• Try a higher offer amount</li>
                <li>• Make a regular payment towards your balance</li>
                <li>• Set up a payment plan to pay over time</li>
                <li>• Contact customer service for assistance</li>
              </ul>
            </div>
          </div>
        </motion.div>

        {/* Action Buttons */}
        <motion.div variants={itemAnimation} className="space-y-3">
          {/* Primary Action - Revise Offer */}
          <button
            onClick={handleReviseOffer}
            className="w-full py-3 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
          >
            <RefreshCw className="w-5 h-5" />
            Revise Your Offer
          </button>

          {/* Secondary Actions */}
          <div className="flex gap-3">
            <button
              onClick={handleMakePayment}
              className="flex-1 py-3 bg-gray-700 hover:bg-gray-600 text-white font-semibold rounded-xl transition-all duration-200 flex items-center justify-center gap-2"
            >
              <DollarSign className="w-5 h-5" />
              Make Regular Payment
            </button>

            <button
              onClick={closeModal}
              className="flex-1 py-3 bg-gray-800 hover:bg-gray-700 text-gray-300 hover:text-white font-semibold rounded-xl transition-all duration-200 flex items-center justify-center gap-2"
            >
              <ArrowLeft className="w-5 h-5" />
              Go Back
            </button>
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default ModalSettlementDeclined;
